/**
 * voice-v8 — Modell-Register (2026-07-29, Audit Paket B2)
 *
 * Vorher standen Modellnamen als Zeichenketten in ueber einem Dutzend Dateien
 * — gemessen: neun verschiedene Namen, darunter laengst ueberholte
 * (`claude-opus-4-6`, `claude-opus-4-7`, `claude-sonnet-4-6`). Jede Umstellung
 * auf ein neues Modell war damit eine Suche quer durch den Code, bei der man
 * eine Stelle uebersieht — und genau so entstand die Kaskade, die auf einem
 * Weg das grosse Modell waehlte und auf dem anderen beim schnellen blieb.
 *
 * Hier steht die EINE Wahrheit. Wer das Modell wechselt, aendert diese Datei —
 * sonst nichts. Ein Test wacht darueber, dass anderswo keine neuen Literale
 * auftauchen.
 *
 * Gewaehlt wird nach ROLLE, nicht nach Name: Der Aufrufer sagt, was er
 * braucht ("sorgfaeltig", "schnell"), nicht welches Modell gerade das beste
 * dafuer ist. Das ueberlebt den naechsten Modellwechsel.
 */

/**
 * Sorgfalt vor Tempo: Dokumente, Rechnungen, Vertraege, Entscheidungen.
 *
 * ⚠️⚠️ 23.09.2026 — auf Opus 5.5 gezogen, auf Martins Wunsch: *„Da wir jetzt
 * das neue Opus 5.5 haben, möchte ich bitte Vespera auf die neue Version
 * anheben."*
 *
 * Auf dem Abo geprüft, bevor hier etwas geändert wurde:
 *
 *   claude -p --model claude-opus-5-5 --effort xhigh …  → ok, lief 5-5
 *   claude -p --model opus …                          → lief 5-5 (!)
 *
 * ⚠️ Die zweite Zeile ist der eigentliche Befund: Das CLI-Update vom
 *    23.09. 06:26 hatte `opus` BEREITS still auf 5.5 umgelegt. Der Chat
 *    (Agent-SDK 0.3.263) fuhr dagegen weiter Opus 5 — Vespera war zweigeteilt,
 *    und dieses Register behauptete für beide Hälften „claude-opus-5".
 *    Seitdem wird die volle Kennung an die CLI gegeben (resolveCliModel),
 *    und das SDK steht auf einer Fassung, die 5.5 kennt.
 *
 * ⚠️ Opus 5.5 denkt ohne Angabe auf `medium`, Opus 5 auf `high`. Den Aufwand
 *    setzt deshalb jeder Weg selbst — siehe shared/denkstufe.ts.
 */
export const MODELL_STARK = "claude-opus-5-5";

/**
 * Der Alltag: Gespraech, Werkzeug-Aufrufe, alles Uebrige.
 *
 * ⚠️ 28.09.2026 auf Sonnet 5.5 gezogen. Martin: *„Sonnet 5.5 ist nun
 * erreichbar. Bitte installiere in Vespera."*
 *
 * Geprüft, bevor hier etwas geändert wurde:
 *
 *   Anthropic-Modellliste         → claude-sonnet-5-5, 2 / 10 $, Denken
 *                                   adaptiv, Vorgabe-Aufwand `high`
 *   claude -p --model claude-sonnet-5-5 …  → ok, modelUsage claude-sonnet-5-5
 *   claude -p --model sonnet …             → lief claude-sonnet-5 (!)
 *   Protokoll 28.09. 22:39 (SDK 0.3.280)   → [cache] … modell=claude-sonnet-5
 *
 * ⚠️ Das Kürzel `sonnet` zeigte also auf BEIDEN Wegen noch auf 5.0. Darum
 *    wird es jetzt wie `opus` gepinnt (resolveCliModel für die CLI,
 *    buildOptions für die warme Sitzung), und das SDK steht auf 0.3.284,
 *    das 5.5 kennt.
 * ⚠️ Der Aufwand ändert sich dadurch nicht still: Jeder Weg setzt ihn
 *    selbst (warme Sitzung `medium`, CLI immer `--effort`).
 */
export const MODELL_SCHNELL = "claude-sonnet-5-5";

/** Kleine, haeufige Aufgaben: Einordnen, Erkennen, Hintergrund-Arbeit. */
/*
 * 08.10.2026 — Haiku 5.5. Kennung ohne Datumsanhang (laut Anthropic-
 * Modellliste ist sie ein fester Stand). Gemessen über `claude -p` (je 5 Läufe,
 * schlank gestartet): Titel 0,7 s statt 7,4 s, Einordnung 1,9 s statt 4,5 s,
 * Kurzantwort 0,8 s statt 1,8 s; Haiku 4.5 denkt dort von sich aus lang.
 * ⚠️ DIE einzige feste Haiku-Kennung im Code — alle anderen Stellen nehmen
 *    diese Konstante (Wächter: tests/server/haiku-5-5-umstieg.test.ts).
 */
export const MODELL_GUENSTIG = "claude-haiku-5-5";

/** Erzaehlend: Buchtexte, Hoerfassungen, lange Prosa.
 *  ⚠️ 2026-09-02 auf 5.1 gezogen — auf der CLI geprüft
 *  (`claude -p --model claude-fable-5-1` → ok). Siehe claude-cli.ts.
 *
 *  ⚠️⚠️ 23.09.2026, Ticket 2516 — Fable 5.1 ist bis auf Weiteres aus dem
 *  System. Martin: *„Fable 5.1 fällt bis auf Weiteres aus dem System.
 *  Buchkapitel, Hörfassungen und lange Prosa laufen künftig ebenfalls auf
 *  Opus 5.5."* Er hatte es schon einmal gesagt; es griff nicht, weil hier
 *  weiter Fable stand und der System-Prompt es darum als Modell für lange
 *  Prosa nannte.
 *
 *  Die ROLLE bleibt, sie fährt jetzt das starke Modell. Kommt Fable zurück,
 *  ist das wieder diese eine Zeile. */
export const MODELL_ERZAEHLEND = MODELL_STARK;

/**
 * Das Kürzel „fable" lebt nur noch als ALTWERT. (23.09.2026, Ticket 2516)
 *
 * Es steht noch in gespeicherten Einstellungen (`starkesModell`,
 * `aboModell`, `cliModel` in users.settings), vielleicht in einer alten
 * `BOOK_COMPOSE_MODEL`-Zeile, und ein Modell kann es aus altem Kontext
 * noch als `model: "fable"` an task_delegate schicken. Nichts davon darf
 * brechen, und nichts davon darf STILL auf ein schwächeres Modell fallen —
 * genau das ist am 23.09. mit einem verlangten Fable schon einmal passiert
 * (task_delegate fiel auf Sonnet). Zur Laufzeit wird daraus Opus.
 *
 * ⚠️ Angeboten und gewählt wird es nirgends mehr. Das ist der EINE Ort, an
 *    dem das Kürzel im Code steht; tests/server/fable-raus.test.ts wacht
 *    darüber.
 */
export const FABLE_ALTWERT = "fable";

/**
 * Ein gespeichertes oder übergebenes „fable" gilt als Opus; alles andere bleibt.
 *
 * ⚠️ Auch die volle Kennung („claude-fable-5-1", „Fable") — nicht nur das
 *    Kürzel. Eine Umgebungsvariable wie CLI_CHAT_MODEL oder ein Modell aus
 *    altem Kontext kann die lange Form tragen; `modellAusKurzform` fing sie
 *    früher über `startsWith("fable")` ab. (Prüfer-Hinweis, 23.09.2026)
 */
export function ohneFable<T>(wert: T): T | "opus" {
  return typeof wert === "string" && wert.toLowerCase().includes(FABLE_ALTWERT)
    ? "opus"
    : wert;
}

/** Bildverstehen (Belege, Screenshots). */
export const MODELL_BILD = MODELL_STARK;
export const MODELL_BILD_FALLBACK = MODELL_GUENSTIG;

/**
 * Die Gemini-Seite (greift, wenn der Abo-Weg ausfaellt — die Anthropic-API
 * ist mangels Guthaben tot). Auch hier gilt die Kaskade: Alltag schnell,
 * Dokumente sorgfaeltig.
 */
/** 14.09.2026 (Martins Frage, live geprüft per Web-Suche — nicht geraten):
 *  Gemini 3.8 Flash ist seit 02.09.2026 GA und löst 3.7 Flash als
 *  Alltags-Modell ab (besser bei Software-Engineering/Agenten/mehrstufigem
 *  Denken, günstiger als die Pro-Stufe). Quelle: ai.google.dev/gemini-api/
 *  docs/models/gemini-3.8-flash. */
export const GEMINI_SCHNELL = "gemini-3.8-flash";
/** ⚠️ NICHT auf 3.5 Pro ziehen (Stand 14.09.2026): laut Google noch "in
 *  Testing", nicht allgemein verfügbar — 3.1 Pro bleibt die aktuelle
 *  GA-Flaggschiff-Stufe (offizieller Google-Blogpost, bestätigt per
 *  Web-Suche). Vor dem nächsten Wechsel erneut pruefen, nicht raten. */
export const GEMINI_STARK = "gemini-3.1-pro-preview";
/** Aktuelle Pro-Stufe mit Werkzeugen. 2.5 ist nicht die letzte gültige. */
export const GEMINI_WERKZEUG = GEMINI_STARK;

/**
 * Die wählbaren Backup-Stufen, wenn das Claude-Abo ausfällt (Ticket 2793).
 *
 * ⚠️ 29.09.2026: Die Auswahl bot „3.7 Flash" an — eine Kennung, die die App
 *    beim Laden als unbekannt verwarf und durch 3.1 Pro ersetzte. Dazu nahmen
 *    die echten Rückfall-Wege die Wahl gar nicht: fest GEMINI_WERKZEUG, und
 *    jedes „flash" wurde ausdrücklich auf Pro gehoben. Martin stellte um, und
 *    es blieb bei 3.1. Die Liste und das Lesen stehen jetzt EINMAL hier —
 *    für Auswahl, Anzeige und Laufzeit.
 */
export const GEMINI_BACKUPS: readonly string[] = [GEMINI_STARK, GEMINI_SCHNELL];

/**
 * Gespeicherte Backup-Wahl → Kennung, die wirklich läuft. Leer oder fremd =
 * Pro. Eine ältere Flash-Fassung (3.7) zieht auf das aktuelle Flash mit,
 * ältere Pro-Stufen (2.5) auf das aktuelle Pro.
 */
export function geminiBackupAus(wert: unknown): string {
  const v = typeof wert === "string" ? wert.trim().toLowerCase() : "";
  if (GEMINI_BACKUPS.includes(v)) return v;
  if (/^gemini-[\d.]+-flash$/.test(v)) return GEMINI_SCHNELL;
  return GEMINI_WERKZEUG;
}

/**
 * xAI/Grok — greift, wenn das Claude-Abo tot ist (Wochenlimit).
 * Kurz: 4.5 (CLI kennt den Namen). Lang/schwer: 4.6.
 */
export const GROK_SCHNELL = "grok-4.5";
export const GROK_STARK = "grok-4.6";

/** Kurzform ("opus", "sonnet") auf den vollen Namen bringen. */
export function modellAusKurzform(kurz: string | null | undefined): string {
  // Ticket 2516 (23.09.2026): ein Altwert „fable" ist Opus — hier stand
  // `startsWith("fable") → MODELL_ERZAEHLEND`.
  const k = ohneFable((kurz ?? "").trim().toLowerCase());
  if (!k) return MODELL_SCHNELL;
  if (k.startsWith("opus")) return MODELL_STARK;
  if (k.startsWith("sonnet")) return MODELL_SCHNELL;
  if (k.startsWith("haiku")) return MODELL_GUENSTIG;
  // Schon ein vollstaendiger Name? Dann unveraendert durchreichen.
  return kurz ?? MODELL_SCHNELL;
}

/**
 * Letzter Stempel vor dem Modell-Aufruf. Überschreibt alles darüber.
 *
 * ⚠️⚠️ 23.09.2026 (Ticket 2515) — die Statuszeile nannte ein anderes Modell
 *    als das, das antwortete.
 *
 *    Gemessen im Sprachweg: buildSystemPrompt schrieb das API-Modell aus
 *    users.settings.model (Haiku 4.5, über modellFuerPrompt) als
 *    `- Modell: "…"` in AKTUELLER STATUS — mit der Anweisung, GENAU diesen
 *    String zu nennen. Geantwortet hat die warme Abo-Sitzung `u1:opus`
 *    mit Opus 5.5. Und dieser Stempel hier kam auf dem warmen Weg gar
 *    nicht an: Die warme Sitzung setzt ihren Prompt nur beim Bau und
 *    stempelte kein Modell. Der Chat-Weg war am 30.08. repariert worden
 *    (Ticket 2411), der Sprachweg nie; der Bereitschafts-Ping baute
 *    Opus-Sitzungen mit „claude-sonnet-5" in der Statuszeile.
 *
 *    Deshalb dreierlei:
 *
 *    1. Die Funktion wohnt jetzt HIER, im Register ohne Importe. In
 *       runtime-modell.ts konnte claude-sdk-session.ts sie nicht holen —
 *       runtime-modell.ts importiert von dort, das wäre ein Zyklus.
 *       runtime-modell.ts reicht sie weiter, alle Aufrufer bleiben gleich.
 *    2. Die Statuszeile wird mit umgeschrieben. Sonst stünden zwei Zeilen
 *       gegeneinander: Der Stempel sagt Opus, der Status sagt Haiku — und
 *       verlangt ausdrücklich, GENAU seinen String zu nennen.
 *    3. „nicht Haiku" ist aus dem Satz gestrichen. Läuft das Abo auf Haiku,
 *       hätte der Stempel sich selbst widersprochen.
 */
export function mitModellWahrheit(prompt: string, id: string): string {
  let block =
    `\n\n## DIESES ZUGES MODELL — verbindlich\n` +
    `Du antwortest in DIESEM Zug mit "${id}". ` +
    `Fragt jemand nach dem Modell, nennst du GENAU diesen Namen — ` +
    `nicht deinen Trainingsstand, nicht ein anderes Modell.`;
  if (/gemini|grok|gpt-/i.test(id)) {
    block +=
      `\n\n## CLAUDE-ABO — verbraucht, nicht gekuendigt\n` +
      `Das Claude-Max-Abo (Cloud/CLI) ist NICHT gekuendigt, NICHT beendet, ` +
      `NICHT ungueltig. Das Wochenkontingent ist verbraucht — deshalb ` +
      `antwortest du als Fallback MIT allen Werkzeugen. Nach dem Reset ist ` +
      `Claude wieder da. Wenn jemand nach dem Abo fragt: sag genau das. ` +
      `Niemals "gekuendigt", "gekündigt", "cancelled", "abgelaufen" oder ` +
      `"beendet" fuer dieses Abo.`;
  }
  return (
    prompt
      .replace(/\n\n## DIESES ZUGES MODELL[\s\S]*$/, "")
      // Als Funktion, nicht als Text: `$` in einer Kennung wäre sonst ein
      // Ersetzungsmuster.
      .replace(/^- Modell: "[^"\n]*"/m, () => `- Modell: "${id}"`) + block
  );
}

/**
 * Was das Modell ueber SICH SELBST wissen muss (Ticket 1691, 2026-08-12).
 *
 * Martin: Vespera behauptete, Claude 3.5 sei die aktuelle Version — waehrend
 * sie selbst auf Claude 5 lief. Das ist kein Denkfehler, sondern die Natur der
 * Sache: Ein Sprachmodell kennt nur seinen Trainingsstand und kann darin
 * unmoeglich stehen haben, was NACH dem Training erschienen ist. Gefragt nach
 * der neuesten Version nennt es die neueste, die es kennt — und klingt dabei
 * sicher.
 *
 * ⚠️ WARUM HIER KEINE GEPFLEGTE LISTE STEHT:
 *
 *   Eine Aufzaehlung "das sind die aktuellen Modelle" waere beim naechsten
 *   Erscheinen falsch — und niemand traegt sie nach, weil sie nur an einer
 *   Stelle im Prompt wirkt und dort niemandem auffaellt. Genau dieselbe
 *   Krankheit wie die veraltete Statusliste in CLAUDE.md.
 *
 *   Darum steht hier nur, was ABLEITBAR ist: die Modelle, die dieses System
 *   TATSAECHLICH benutzt. Die stehen ohnehin oben in dieser Datei, werden bei
 *   jedem Wechsel geaendert, und ein Test wacht darueber. Dazu die ehrliche
 *   Auskunft ueber die Grenze des eigenen Wissens.
 */
export function modellSelbstwissen(): string {
  /*
   * ⚠️ 23.09.2026, Ticket 2516 — EINE Zeile je MODELL, nicht je Rolle.
   *    Hier stand `- claude-fable-5-1 — lange Prosa (Buchtexte,
   *    Hoerfassungen)`, und Vespera nannte Fable weiter als Buch-Modell,
   *    nachdem Martin es abbestellt hatte. Seit die erzählende Rolle das
   *    starke Modell fährt, stünde Opus 5.5 sonst zweimal da.
   */
  const rollen: ReadonlyArray<readonly [string, string]> = [
    [MODELL_STARK, "Sorgfalt vor Tempo (Dokumente, Rechnungen, Entscheidungen)"],
    [MODELL_SCHNELL, "der Alltag (Gespraech, Werkzeuge)"],
    [MODELL_GUENSTIG, "kleine, haeufige Aufgaben im Hintergrund"],
    [MODELL_ERZAEHLEND, "lange Prosa (Buchtexte, Hoerfassungen)"],
  ];
  const jeModell = new Map<string, string[]>();
  for (const [id, rolle] of rollen) {
    jeModell.set(id, [...(jeModell.get(id) ?? []), rolle]);
  }
  const liste = [...jeModell]
    .map(([id, r]) => `- ${id} — ${r.join("; ")}\n`)
    .join("");
  return (
    `\n## WAS DU UEBER MODELLE WEISST — UND WAS NICHT\n` +
    `Du laeufst auf einem Modell der Claude-Familie von Anthropic. Dieses ` +
    `System verwendet:\n` +
    liste +
    `\n⚠️ DEIN TRAININGSWISSEN ZU MODELLVERSIONEN IST VERALTET — immer. ` +
    `Was nach deinem Training erschien, kann darin nicht stehen. Fragt dich ` +
    `jemand nach der neuesten Claude-Version, nenne NICHT die neueste, die du ` +
    `aus dem Training kennst: Das war schon falsch (Vespera behauptete "3.5", ` +
    `waehrend sie auf 5 lief). Nenne die Liste oben — das ist belegt — und ` +
    `sage dazu, dass du fuer alles Darueberhinausgehende nachsehen musst. ` +
    `Bei Fragen zu Preisen, Grenzen oder Faehigkeiten anderer Modelle gilt ` +
    `dasselbe: nachsehen statt aus dem Gedaechtnis antworten.\n`
  );
}
